import {
  CashReconciliationDetail,
  CashReconciliationSummary,
  FinanceLateReport,
  ReportStatus,
} from '../Model/cashReconciliationXModel'
import { FinanceBXInterceptor } from '../Config/FinanceBXInterceptor'

const financeReportStatuses: ReportStatus[] = [
  'ASSOCIATE_APPROVED',
  'FINANCE_CHANGES_REQUESTED',
  'PUBLISHED',
]

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount)
}

export const cashReconciliationBXService = {
  list: async (params: {
    page: number
    limit: number
    status?: ReportStatus
    reportDate?: string
  }) => {
    // Finance queue: GET /api/v1/finance/station-reports
    // status is omitted for work queue (defaults to ASSOCIATE_APPROVED on backend),
    // status=PUBLISHED for history.
    const queryParams = new URLSearchParams()
    queryParams.set('page', String(params.page))
    queryParams.set('limit', String(params.limit))

    if (params.status) {
      queryParams.set('status', params.status)
    }

    if (params.reportDate) {
      queryParams.set('reportDate', params.reportDate)
    }

    return FinanceBXInterceptor.get<{
      data: CashReconciliationSummary[]
      meta: { total: number; page: number; limit: number }
    }>(`/finance/station-reports?${queryParams.toString()}`)
  },

  listAll: async (params: { status?: ReportStatus; reportDate?: string }) => {
    const limit = 100
    const firstResponse = await cashReconciliationBXService.list({ ...params, page: 1, limit })
    const firstPage = firstResponse.data.data ?? []
    const total = firstResponse.data.meta?.total ?? firstPage.length
    const pageSize = firstResponse.data.meta?.limit ?? limit
    const totalPages = Math.max(1, Math.ceil(total / pageSize))
    const remainingResponses = await Promise.all(
      Array.from({ length: totalPages - 1 }, (_, index) =>
        cashReconciliationBXService.list({ ...params, page: index + 2, limit }),
      ),
    )

    return [...firstPage, ...remainingResponses.flatMap((response) => response.data.data ?? [])]
  },

  listAllFinanceStatuses: async () => {
    const responses = await Promise.all(
      financeReportStatuses.map((status) => cashReconciliationBXService.listAll({ status })),
    )
    const uniqueReports = new Map<string, CashReconciliationSummary>()

    responses.flat().forEach((report) => uniqueReports.set(report.resourceId, report))

    return Array.from(uniqueReports.values()).sort((left, right) => {
      const reportDateComparison = right.reportDate.localeCompare(left.reportDate)
      return reportDateComparison || right.createdAt.localeCompare(left.createdAt)
    })
  },

  listLate: async (params: { page?: number; limit?: number; stationId?: string } = {}) => {
    const queryParams = new URLSearchParams()
    queryParams.set('page', String(params.page ?? 1))
    queryParams.set('limit', String(params.limit ?? 1))

    if (params.stationId) {
      queryParams.set('stationId', params.stationId)
    }

    return FinanceBXInterceptor.get<{
      data: FinanceLateReport[]
      meta: { total: number; page: number; limit: number }
    }>(`/finance/station-reports/late?${queryParams.toString()}`)
  },

  getById: async (id: string) => {
    return FinanceBXInterceptor.get<{ data: CashReconciliationDetail }>(
      `/finance/station-reports/${encodeURIComponent(id)}`,
    )
  },

  updateConfirmedDeposit: async (
    id: string,
    payload: { confirmedCashDepositMinor: number; overrideReason?: string },
  ) => {
    return FinanceBXInterceptor.put<{ data: CashReconciliationDetail }>(
      `/finance/station-reports/${encodeURIComponent(id)}/confirmed-cash-deposit`,
      payload,
    )
  },

  approve: async (id: string) => {
    return FinanceBXInterceptor.post<{ data: CashReconciliationDetail }>(
      `/finance/station-reports/${encodeURIComponent(id)}/approve`,
    )
  },

  reject: async (id: string, payload: { note: string }) => {
    return FinanceBXInterceptor.post<{ data: CashReconciliationDetail }>(
      `/finance/station-reports/${encodeURIComponent(id)}/reject`,
      payload,
    )
  },

  retrySettlement: async (id: string) => {
    return FinanceBXInterceptor.post<{ data: CashReconciliationDetail }>(
      `/finance/station-reports/${encodeURIComponent(id)}/settlement/retry`,
    )
  },
}
